// Notification data access (FR-NTF-01/02).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { useRealtimeEvent, useRealtimeStatus } from '@/lib/realtime/context';

export interface Notification {
  id: string;
  kind: string;
  subject: string;
  body: string;
  link_path?: string;
  entity_type?: string;
  entity_id?: string;
  read_at?: string;
  created_at: string;
}

export interface Inbox {
  items: Notification[];
  unread: number;
}

export interface NotificationPreference {
  kind: string;
  email_enabled: boolean;
  in_app_enabled: boolean;
}

/**
 * What each notification kind is, in the words somebody would use for it.
 *
 * The server sends event names — `LeaveDecided`, `DSRRaised` — because that is
 * the vocabulary modules publish in. Translating them here rather than on the
 * server keeps the event names stable for consumers that are not this screen.
 */
export const KIND_LABEL: Record<string, string> = {
  EmployeeInvited: 'Your account',
  LeaveDecided: 'Leave decisions',
  AttendanceCorrectionDecided: 'Attendance corrections',
  DSRRaised: 'New privacy requests',
  DSRResolved: 'Your privacy requests',
  CandidateInvited: 'Joining invitations',
  chat: 'Chat: unread messages',
  tasks: 'Tasks: daily reminder',
};

/**
 * Pushed when the live connection is up, polled when it is not (FR-NTF-03).
 *
 * The bell used to poll every thirty seconds, and this comment argued a
 * WebSocket was "not worth it for a bell". Collaboration needs the connection
 * anyway (ADR 0005), so the bell rides on it: a notification.created or
 * notification.read event refetches the inbox at once — including when the
 * person reads it on another device. The poll stays as the fallback, slowed to
 * two minutes while the connection is live, in case an event is ever missed.
 */
export function useInbox() {
  const queryClient = useQueryClient();
  const status = useRealtimeStatus();

  useRealtimeEvent(['notification.created', 'notification.read'], () => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  });

  return useQuery({
    queryKey: ['notifications'] as const,
    queryFn: () => api.get<Inbox>('/notifications?limit=50'),
    refetchInterval: status === 'live' ? 120_000 : 30_000,
    // Coming back to the tab is exactly when somebody wants the count to be
    // right, so it refetches then too.
    refetchOnWindowFocus: true,
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/notifications/${id}/read`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/notifications/read-all'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function usePreferences() {
  return useQuery({
    queryKey: ['notifications', 'preferences'] as const,
    queryFn: () => api.get<{ items: NotificationPreference[]; note: string }>(
      '/notifications/preferences',
    ),
  });
}

export function useSetPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pref: NotificationPreference) =>
      api.put<void>('/notifications/preferences', pref),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'preferences'] }),
  });
}
